/**
 * PersonalMemoryPage ✦ — iter210
 *
 * The only destination a guest sees after contributing. Replaces
 * the iter-era QRContinuity screen (which surfaced "tonight's
 * gallery" as the QR target) with a page that simply says:
 *
 *   "This memory belongs to you."
 *
 * Constitutional brief (iter210):
 *
 *   • Never redirect guests to the Memory Wall.
 *   • Never redirect guests to galleries.
 *   • Personal phone (QR scan) = just the memory + lineage. No
 *     "Pass to the next memory ✦" gesture.
 *   • Shared kiosk (?kiosk=1) = same memory + lineage + a "Pass
 *     to the next memory ✦" pill that returns the booth to its
 *     launcher for the next guest.
 *   • Lineage section (architectural lock): "This may become part
 *     of..." or "This became part of..." surfacing Final Night ✦
 *     / Silent Film ✦ / Documentary ✦ based on the event package
 *     and what's already settled. The bridge between participation
 *     and preservation.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { QRCodeSVG } from 'qrcode.react';
import EventDropAttribution from '../components/EventDropAttribution';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Locked emotional theme — matches the Booth OS QRContinuity look
// so the transition between Booth OS and the Personal Memory Page
// feels continuous, not like a different surface.
const BG = '#1e120a';
const POLAROID = '#f3ead2';
const INK = '#f8efd6';
const INK_SOFT = 'rgba(248,239,214,0.74)';
const INK_FAINT = 'rgba(248,239,214,0.55)';
const GOLD = '#caa05e';
const SERIF = "'Cormorant Garamond','Playfair Display',Georgia,serif";

// Type-specific page chrome (eyebrow + heading + QR caption).
// Constitutional copy per iter211 brief — the QR is the ownership
// mechanism, not a return ticket. Each caption answers the
// guest's mental model: "this is mine to keep".
const TYPE_COPY = {
  photo:         { eyebrow: 'PRESERVED', heading: 'This photograph is yours ✦',     qrCaption: 'Keep this strip ✦' },
  guestbook:     { eyebrow: 'PRESERVED', heading: 'This voice is yours ✦',           qrCaption: 'Keep this recording ✦' },
  lens_note:     { eyebrow: 'PRESERVED', heading: 'Your handwriting will stay ✦',    qrCaption: 'Keep this memory ✦' },
  drop_one_word: { eyebrow: 'PRESERVED', heading: 'A feeling, left behind ✦',        qrCaption: 'Keep this word ✦' },
  remember_us:   { eyebrow: 'PRESERVED', heading: 'Your mark joined the wall ✦',     qrCaption: 'Keep this memory ✦' },
  memory_snap:   { eyebrow: 'PRESERVED', heading: 'A Polaroid, kept ✦',              qrCaption: 'Keep this Polaroid ✦' },
  flip_guestbook: { eyebrow: 'PRESERVED', heading: 'Your page is yours ✦',           qrCaption: 'Keep this page ✦' },
};

const ARTEFACT_NAMES = {
  final_night: 'The Final Night ✦',
  silent_film: 'Silent Film ✦',
  documentary: 'The Documentary ✦',
};

const resolveMedia = (rel) => {
  if (!rel) return null;
  if (rel.startsWith('http')) return rel;
  if (rel.startsWith('/api/')) return `${BACKEND_URL}${rel}`;
  return `${BACKEND_URL}/api/photos/${rel}`;
};

// Type-specific contribution body. Each branch renders the
// guest's own contribution centred above the QR. Never any gallery
// thumbnails, never any "other contributions".
const ContributionBody = ({ contribution }) => {
  if (!contribution) return null;
  const url = resolveMedia(contribution.url || contribution.storage_path);
  const videoUrl = resolveMedia(contribution.video_url);
  const audioUrl = resolveMedia(contribution.audio_url);

  if (contribution.kind === 'photo') {
    return (
      <img
        data-testid="personal-memory-photo"
        src={url}
        alt=""
        style={{
          display: 'block', maxWidth: '78vw', maxHeight: '46vh',
          width: 'auto', height: 'auto', objectFit: 'contain',
          boxShadow: '0 24px 60px rgba(0,0,0,0.5)',
        }}
      />
    );
  }
  if (contribution.kind === 'guestbook') {
    if (videoUrl) {
      return (
        <video
          data-testid="personal-memory-video"
          src={videoUrl}
          controls
          playsInline
          style={{ maxWidth: '78vw', maxHeight: '46vh', background: '#0b0805' }}
        />
      );
    }
    return (
      <div style={{ textAlign: 'center', maxWidth: '78vw' }}>
        {audioUrl && (
          <audio
            data-testid="personal-memory-audio"
            src={audioUrl}
            controls
            style={{ display: 'block', margin: '0 auto' }}
          />
        )}
        {contribution.message && (
          <p style={{
            margin: '24px auto 0', maxWidth: 480,
            fontFamily: SERIF, fontStyle: 'italic',
            fontSize: 'clamp(17px,2vw,21px)', lineHeight: 1.55,
            color: INK_SOFT,
          }}>
            "{contribution.message}"
          </p>
        )}
      </div>
    );
  }
  if (contribution.kind === 'lens_note') {
    return (
      <div style={{ textAlign: 'center', maxWidth: '78vw' }}>
        {videoUrl && (
          <video
            data-testid="personal-memory-lens-video"
            src={videoUrl}
            controls
            playsInline
            style={{ maxWidth: '78vw', maxHeight: '40vh', background: '#0b0805' }}
          />
        )}
        {contribution.message && (
          <p style={{
            margin: '24px auto 0', maxWidth: 480,
            fontFamily: SERIF, fontStyle: 'italic',
            fontSize: 'clamp(17px,2vw,21px)', lineHeight: 1.55,
            color: INK_SOFT,
          }}>
            "{contribution.message}"
          </p>
        )}
      </div>
    );
  }
  if (contribution.kind === 'drop_one_word') {
    return (
      <p
        data-testid="personal-memory-word"
        style={{
          margin: 0,
          fontFamily: SERIF, fontStyle: 'italic',
          fontSize: 'clamp(48px,8vw,84px)', lineHeight: 1.05,
          color: GOLD, letterSpacing: '-0.01em',
        }}
      >
        {contribution.word}
      </p>
    );
  }
  if (contribution.kind === 'remember_us') {
    return contribution.preview ? (
      <img
        data-testid="personal-memory-stroke"
        src={resolveMedia(contribution.preview)}
        alt=""
        style={{ maxWidth: '78vw', maxHeight: '46vh' }}
      />
    ) : (
      <p style={{ fontFamily: SERIF, fontStyle: 'italic', color: INK_SOFT }}>
        Your mark sits on the wall.
      </p>
    );
  }
  return null;
};

// Lineage section. Past tense = "became part of", future tense =
// "may become part of". Always restrained, never marketing.
const Lineage = ({ lineage }) => {
  if (!lineage) return null;
  const became = lineage.became_part_of || [];
  const may = lineage.may_become_part_of || [];
  if (!became.length && !may.length) return null;

  return (
    <section
      data-testid="personal-memory-lineage"
      style={{
        marginTop: 'clamp(36px,5vw,56px)',
        maxWidth: 460, textAlign: 'center',
      }}
    >
      {became.length > 0 && (
        <>
          <p style={{
            margin: 0, fontFamily: SERIF, fontStyle: 'italic',
            fontSize: 11, letterSpacing: '0.24em', textTransform: 'uppercase',
            color: INK_FAINT,
          }}>
            This memory became part of
          </p>
          <ul
            data-testid="lineage-became"
            style={{ listStyle: 'none', margin: '14px 0 0', padding: 0 }}
          >
            {became.map((k) => (
              <li key={k} style={{
                fontFamily: SERIF, fontStyle: 'italic',
                fontSize: 'clamp(17px,2vw,20px)',
                color: INK, margin: '6px 0',
              }}>
                {ARTEFACT_NAMES[k] || k}
              </li>
            ))}
          </ul>
        </>
      )}
      {may.length > 0 && (
        <div style={{ marginTop: became.length ? 28 : 0 }}>
          <p style={{
            margin: 0, fontFamily: SERIF, fontStyle: 'italic',
            fontSize: 11, letterSpacing: '0.24em', textTransform: 'uppercase',
            color: INK_FAINT,
          }}>
            This memory may become part of
          </p>
          <ul
            data-testid="lineage-may-become"
            style={{ listStyle: 'none', margin: '14px 0 0', padding: 0 }}
          >
            {may.map((k) => (
              <li key={k} style={{
                fontFamily: SERIF, fontStyle: 'italic',
                fontSize: 'clamp(15px,1.8vw,18px)',
                color: INK_SOFT, margin: '6px 0',
              }}>
                {ARTEFACT_NAMES[k] || k}
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
};


// ── Page ────────────────────────────────────────────────────────
const PersonalMemoryPage = () => {
  const { slug, contribution_id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  // iter210 — kiosk context propagates via ?kiosk=1. Personal-device
  // QR scans never carry the param, so the Pass button is absent.
  const isKiosk = searchParams.get('kiosk') === '1';

  const [state, setState] = useState({ status: 'loading', payload: null, error: null });

  useEffect(() => {
    let aborted = false;
    (async () => {
      try {
        const ev = await axios.get(`${API}/events/slug/${slug}`);
        if (aborted) return;
        const r = await axios.get(
          `${API}/events/${ev.data.event_id}/contributions/${contribution_id}`,
        );
        if (aborted) return;
        setState({ status: 'ready', payload: r.data, error: null });
      } catch (e) {
        if (!aborted) {
          setState({
            status: 'error',
            payload: null,
            error: e?.response?.data?.detail || "This memory hasn't settled yet ✦",
          });
        }
      }
    })();
    return () => { aborted = true; };
  }, [slug, contribution_id]);

  // iter211 ✦ — Direct artefact retrieval. The QR encodes the
  // backend's artefact endpoint, not a route back to this page. A
  // scan opens the composed memory directly (image / video / PDF)
  // in the phone's native viewer — the OS's save/share controls
  // then handle ownership. The QR is no longer a "return ticket";
  // it is the ownership mechanism.
  const canonicalUrl = useMemo(() => {
    if (typeof window === 'undefined') return '';
    const eventId = state.payload?.event_id;
    if (!eventId) {
      // Until the contribution is resolved we point at the page
      // itself so the QR component renders something valid; once
      // the payload settles, the QR re-renders against the
      // artefact endpoint. Scans never happen during the loading
      // window in practice (the QR is only printed after the page
      // is ready).
      return `${window.location.origin}/event/${slug}/m/${contribution_id}`;
    }
    return `${BACKEND_URL}/api/events/${eventId}/contributions/${contribution_id}/artefact`;
  }, [slug, contribution_id, state.payload]);

  const copy = useMemo(() => {
    const kind = state.payload?.contribution?.kind;
    return TYPE_COPY[kind] || {
      eyebrow: 'PRESERVED',
      heading: 'This memory is yours ✦',
      qrCaption: 'Return to your memory ✦',
    };
  }, [state.payload]);

  return (
    <div
      data-testid="personal-memory-page"
      data-kiosk={isKiosk ? '1' : '0'}
      style={{
        minHeight: '100vh', background: BG, color: INK,
        fontFamily: SERIF,
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        textAlign: 'center', padding: 'clamp(40px, 6vw, 80px) 24px',
      }}
    >
      <p
        data-testid="personal-memory-eyebrow"
        style={{
          margin: 0,
          fontSize: 11, letterSpacing: '0.32em', textTransform: 'uppercase',
          color: GOLD, fontStyle: 'italic',
        }}
      >
        ✦ {copy.eyebrow}
      </p>
      <h1
        data-testid="personal-memory-heading"
        style={{
          margin: '20px 0 0',
          fontFamily: SERIF, fontWeight: 400,
          fontSize: 'clamp(34px,5vw,56px)', lineHeight: 1.08,
          letterSpacing: '-0.012em', color: INK,
        }}
      >
        {copy.heading}
      </h1>

      <div style={{ marginTop: 'clamp(28px,4vw,44px)' }}>
        {state.status === 'loading' && (
          <p data-testid="personal-memory-loading" style={{
            fontStyle: 'italic', color: INK_FAINT, fontSize: 16,
          }}>
            Gathering ✦
          </p>
        )}
        {state.status === 'error' && (
          <p data-testid="personal-memory-error" style={{
            fontStyle: 'italic', color: INK_FAINT, fontSize: 16,
          }}>
            {state.error}
          </p>
        )}
        {state.status === 'ready' && (
          <ContributionBody contribution={state.payload?.contribution} />
        )}
      </div>

      {/* QR — links to the canonical (kiosk-stripped) URL. */}
      {state.status === 'ready' && (
        <div
          style={{
            marginTop: 'clamp(36px,5vw,56px)',
            padding: 'clamp(18px,2vw,26px)',
            background: POLAROID, borderRadius: 6,
            boxShadow: '0 24px 60px rgba(0,0,0,0.45)',
          }}
        >
          <QRCodeSVG
            value={canonicalUrl}
            size={Math.min(220, typeof window !== 'undefined' ? window.innerWidth * 0.5 : 220)}
            level="M"
            fgColor={BG}
            bgColor={POLAROID}
            data-testid="personal-memory-qr"
          />
          <div style={{
            marginTop: 12,
            fontFamily: SERIF, fontStyle: 'italic',
            fontSize: 12, color: '#3F2A14', letterSpacing: '0.16em',
            textTransform: 'uppercase',
          }}>
            ✦ {state.payload.event_name} ✦
          </div>
        </div>
      )}
      {state.status === 'ready' && (
        <p
          data-testid="personal-memory-qr-caption"
          style={{
            marginTop: 22, fontFamily: SERIF, fontStyle: 'italic',
            fontSize: 14, color: INK_SOFT, letterSpacing: '0.04em',
          }}
        >
          {copy.qrCaption}
        </p>
      )}

      {/* Lineage — bridge between participation and preservation. */}
      {state.status === 'ready' && <Lineage lineage={state.payload?.lineage} />}

      {/* Kiosk-only — Pass to the next memory ✦. Absent on personal
          phones; present only when ?kiosk=1 is on the URL. */}
      {isKiosk && state.status === 'ready' && (
        <div style={{ marginTop: 'clamp(36px,5vw,56px)' }}>
          <button
            data-testid="personal-memory-pass-button"
            onClick={() => navigate(`/booth-os/${state.payload.event_id}`)}
            style={{
              padding: '14px 36px',
              fontFamily: SERIF, fontStyle: 'italic',
              fontSize: 16, color: BG,
              background: GOLD, border: 'none', borderRadius: 999,
              cursor: 'pointer', letterSpacing: '0.02em',
              boxShadow: '0 12px 28px rgba(0,0,0,0.35)',
            }}
          >
            Pass to the next memory ✦
          </button>
        </div>
      )}
      <EventDropAttribution />
    </div>
  );
};

export default PersonalMemoryPage;
